import { IconCheck, IconChevronRight } from "@tabler/icons-react";
import { Link } from "@tanstack/react-router";
import type { TFunction } from "i18next";
import type React from "react";
import { MarketplaceBadge } from "@/components/shared/marketplace-badge";
import { Checkbox } from "@/components/ui/checkbox";
import { cn } from "@/lib/utils";
import {
  type CheckState,
  checkStateOf,
  type FilteredAccount,
  type FilteredTree,
  type ScopeAccount,
  type ScopeShop,
} from "./model";

/**
 * Popover rows for the shop scope (Paper "Popover: account → marketplace tree").
 * Lanes: 6px inset · 16px chevron slot · 8px · 18px checkbox · 8px · content,
 * so marketplace rows (56px inset) line their checkbox up under the account name.
 */

const ROW =
  "group/row flex w-full shrink-0 items-center gap-2 rounded-xs pr-2 text-left outline-none";
const ROW_INTERACTIVE =
  "cursor-pointer hover:bg-control-highlight focus-visible:bg-control-highlight has-[:focus-visible]:bg-control-highlight";
const BADGE_ON_HIGHLIGHT =
  "group-hover/row:bg-surface group-focus-visible/row:bg-surface group-has-[:focus-visible]/row:bg-surface";
const CHECKBOX_DISABLED = "data-disabled:border-line data-disabled:bg-subtle";

export interface ScopeTreeLabels {
  t: TFunction<"dashboard">;
  countryName: (code: string) => string;
}

function shopPrimaryText(shop: ScopeShop, account: ScopeAccount | null, labels: ScopeTreeLabels) {
  // Normally every marketplace of an account shares its name, so the country
  // carries the row; legacy diverging names stay visible instead.
  if (!account || shop.name !== account.name || !shop.marketplace) return shop.name;
  return labels.countryName(shop.marketplace);
}

export function shopAccessibleName(shop: ScopeShop, labels: ScopeTreeLabels): string {
  return shop.marketplace ? `${shop.name} · ${labels.countryName(shop.marketplace)}` : shop.name;
}

function accountSubtitle(
  entry: FilteredAccount,
  searching: boolean,
  { t }: ScopeTreeLabels,
): string {
  const { account } = entry;
  if (account.needsReconnect) return t("shopScope.authExpired");
  if (searching && !entry.accountMatched) {
    return t("shopScope.marketplacesMatch", {
      matched: entry.shops.length,
      count: account.shops.length,
    });
  }
  return [
    account.accountType ? t(`shopScope.accountType.${account.accountType}`) : null,
    account.regions.length > 0
      ? account.regions.map((region) => t(`shopScope.region.${region}`)).join(", ")
      : null,
    t("shopScope.marketplaceCount", { count: account.shops.length }),
  ]
    .filter(Boolean)
    .join(" · ");
}

function ReconnectBadge({
  label,
  onNavigate,
}: {
  label: string;
  /** Absent inside a button row, where a nested link would be invalid. */
  onNavigate?: () => void;
}) {
  const className =
    "inline-flex h-[22px] shrink-0 items-center rounded-full bg-warning-background px-2 text-badge font-medium text-warning-foreground";
  if (!onNavigate) return <span className={className}>{label}</span>;
  return (
    <Link
      to="/settings"
      onClick={(event: React.MouseEvent) => {
        event.stopPropagation();
        onNavigate();
      }}
      className={cn(
        className,
        "outline-none hover:underline focus-visible:ring-2 focus-visible:ring-focus-ring",
      )}
    >
      {label}
    </Link>
  );
}

function OnlyButton({
  name,
  onOnly,
  t,
}: {
  name: string;
  onOnly: () => void;
  t: ScopeTreeLabels["t"];
}) {
  return (
    <button
      type="button"
      onClick={(event) => {
        event.stopPropagation();
        onOnly();
      }}
      aria-label={t("shopScope.onlyLabel", { name })}
      className="inline-flex h-5 shrink-0 cursor-pointer items-center rounded-xs bg-surface px-1.5 text-badge font-medium text-ink opacity-0 outline-none ring-1 ring-line transition-opacity duration-[var(--duration-fast)] group-hover/row:opacity-100 group-has-[:focus-visible]/row:opacity-100 focus-visible:ring-2 focus-visible:ring-focus-ring pointer-coarse:opacity-100"
    >
      {t("shopScope.only")}
    </button>
  );
}

function Chevron({ expanded }: { expanded: boolean }) {
  return (
    <IconChevronRight
      className={cn(
        "size-3.5 text-muted-foreground transition-transform duration-[var(--duration-fast)]",
        expanded && "rotate-90",
      )}
      stroke={2}
      aria-hidden
    />
  );
}

function ScopeCheckbox({
  state,
  disabled,
  label,
  onToggle,
  accountId,
}: {
  state: CheckState;
  disabled: boolean;
  label: string;
  onToggle: () => void;
  accountId?: string;
}) {
  return (
    <Checkbox
      checked={state === "checked"}
      indeterminate={state === "indeterminate"}
      disabled={disabled}
      onCheckedChange={onToggle}
      // The row toggles on click too; stop the bubble to avoid a double toggle.
      onClick={(event) => event.stopPropagation()}
      aria-label={label}
      data-scope-nav=""
      data-scope-account={accountId}
      className={CHECKBOX_DISABLED}
    />
  );
}

export interface MultiScopeTreeProps {
  filtered: FilteredTree;
  searching: boolean;
  draft: ReadonlySet<string>;
  expanded: ReadonlySet<string>;
  /** Hide "Only" when it could only re-select the whole universe. */
  showOnly: { accounts: boolean; shops: boolean };
  disabled: boolean;
  labels: ScopeTreeLabels;
  onToggleExpanded: (accountId: string) => void;
  onToggleShops: (shops: readonly ScopeShop[]) => void;
  onOnly: (shops: readonly ScopeShop[]) => void;
  onNavigate: () => void;
}

export function MultiScopeTree({
  filtered,
  searching,
  draft,
  expanded,
  showOnly,
  disabled,
  labels,
  onToggleExpanded,
  onToggleShops,
  onOnly,
  onNavigate,
}: MultiScopeTreeProps) {
  const { t } = labels;
  return (
    <>
      {filtered.accounts.map((entry) => {
        const { account } = entry;
        const isOpen = searching || expanded.has(account.id);
        const accountDisabled = disabled || account.needsReconnect;
        return (
          // biome-ignore lint/a11y/useSemanticElements: a fieldset would add form chrome; this only names the account's rows for assistive tech.
          <div key={account.id} role="group" aria-label={account.name} className="flex flex-col">
            {/* biome-ignore lint/a11y/noStaticElementInteractions: the checkbox is the accessible control; the row is a wider hit area. */}
            {/* biome-ignore lint/a11y/useKeyWithClickEvents: keyboard users operate the focusable checkbox itself. */}
            <div
              onClick={() =>
                accountDisabled ? onToggleExpanded(account.id) : onToggleShops(account.shops)
              }
              className={cn(ROW, "h-10 pl-1.5", ROW_INTERACTIVE)}
            >
              <button
                type="button"
                // Keyboard expands via →/← on the checkbox; a blocked account has
                // no focusable checkbox, so its chevron takes the stop instead.
                tabIndex={accountDisabled ? undefined : -1}
                data-scope-nav={accountDisabled ? "" : undefined}
                data-scope-account={accountDisabled ? account.id : undefined}
                disabled={searching}
                aria-expanded={isOpen}
                aria-label={t(isOpen ? "shopScope.collapse" : "shopScope.expand", {
                  name: account.name,
                })}
                onClick={(event) => {
                  event.stopPropagation();
                  onToggleExpanded(account.id);
                }}
                className="flex size-4 shrink-0 cursor-pointer items-center justify-center rounded-[4px] outline-none focus-visible:ring-2 focus-visible:ring-focus-ring disabled:cursor-default"
              >
                <Chevron expanded={isOpen} />
              </button>
              <ScopeCheckbox
                state={checkStateOf(account.shops, draft)}
                disabled={accountDisabled}
                label={account.name}
                onToggle={() => onToggleShops(account.shops)}
                accountId={account.id}
              />
              <span className="flex min-w-0 flex-1 flex-col gap-px">
                <span
                  className={cn(
                    "truncate text-xs font-semibold",
                    account.needsReconnect ? "text-placeholder" : "text-ink",
                  )}
                >
                  {account.name}
                </span>
                <span className="truncate text-badge text-muted-foreground tabular-nums">
                  {accountSubtitle(entry, searching, labels)}
                </span>
              </span>
              {account.needsReconnect ? (
                <ReconnectBadge label={t("shopScope.reconnect")} onNavigate={onNavigate} />
              ) : showOnly.accounts && !disabled ? (
                <OnlyButton name={account.name} onOnly={() => onOnly(account.shops)} t={t} />
              ) : null}
            </div>
            {isOpen
              ? entry.shops.map((shop) => (
                  <MultiShopRow
                    key={shop.shopId}
                    shop={shop}
                    account={account}
                    checked={draft.has(shop.shopId)}
                    showOnly={showOnly.shops}
                    disabled={disabled}
                    labels={labels}
                    onToggle={() => onToggleShops([shop])}
                    onOnly={() => onOnly([shop])}
                    onNavigate={onNavigate}
                  />
                ))
              : null}
          </div>
        );
      })}
      {filtered.ungrouped.map((shop) => (
        <MultiShopRow
          key={shop.shopId}
          shop={shop}
          account={null}
          checked={draft.has(shop.shopId)}
          showOnly={showOnly.shops}
          disabled={disabled}
          labels={labels}
          onToggle={() => onToggleShops([shop])}
          onOnly={() => onOnly([shop])}
          onNavigate={onNavigate}
        />
      ))}
    </>
  );
}

function MultiShopRow({
  shop,
  account,
  checked,
  showOnly,
  disabled,
  labels,
  onToggle,
  onOnly,
  onNavigate,
}: {
  shop: ScopeShop;
  account: ScopeAccount | null;
  checked: boolean;
  showOnly: boolean;
  disabled: boolean;
  labels: ScopeTreeLabels;
  onToggle: () => void;
  onOnly: () => void;
  onNavigate: () => void;
}) {
  const { t } = labels;
  const blocked = disabled || shop.needsReconnect;
  // A wholly expired account carries one Reconnect badge on its header.
  const showReconnect = shop.needsReconnect && !account?.needsReconnect;
  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: the checkbox is the accessible control; the row is a wider hit area.
    // biome-ignore lint/a11y/useKeyWithClickEvents: keyboard users operate the focusable checkbox itself.
    <div
      onClick={blocked ? undefined : onToggle}
      className={cn(
        ROW,
        "h-8",
        account ? "pl-14" : "pl-7.5",
        blocked ? "cursor-default" : ROW_INTERACTIVE,
      )}
    >
      <ScopeCheckbox
        state={checked ? "checked" : "unchecked"}
        disabled={blocked}
        label={shopAccessibleName(shop, labels)}
        onToggle={onToggle}
      />
      {shop.marketplace ? (
        <MarketplaceBadge code={shop.marketplace} className={cn(!blocked && BADGE_ON_HIGHLIGHT)} />
      ) : null}
      <span className="flex min-w-0 flex-1 items-baseline gap-1.5">
        <span
          className={cn("truncate text-xs", shop.needsReconnect ? "text-placeholder" : "text-ink")}
        >
          {shopPrimaryText(shop, account, labels)}
        </span>
        {shop.domain ? (
          <span className="truncate text-badge text-muted-foreground">{shop.domain}</span>
        ) : null}
      </span>
      {showReconnect ? (
        <ReconnectBadge label={t("shopScope.reconnect")} onNavigate={onNavigate} />
      ) : showOnly && !blocked ? (
        <OnlyButton name={shopAccessibleName(shop, labels)} onOnly={onOnly} t={t} />
      ) : null}
    </div>
  );
}

export interface SingleScopeListProps {
  filtered: FilteredTree;
  searching: boolean;
  value: string | undefined;
  expanded: ReadonlySet<string>;
  disabled: boolean;
  labels: ScopeTreeLabels;
  onToggleExpanded: (accountId: string) => void;
  onPick: (shopId: string) => void;
}

/** Single-Shop picker rows: same tree, a Select-style check lane instead of checkboxes. */
export function SingleScopeList({
  filtered,
  searching,
  value,
  expanded,
  disabled,
  labels,
  onToggleExpanded,
  onPick,
}: SingleScopeListProps) {
  const { t } = labels;
  return (
    <>
      {filtered.accounts.map((entry) => {
        const { account } = entry;
        const isOpen = searching || expanded.has(account.id);
        return (
          // biome-ignore lint/a11y/useSemanticElements: a fieldset would add form chrome; this only names the account's rows for assistive tech.
          <div key={account.id} role="group" aria-label={account.name} className="flex flex-col">
            <button
              type="button"
              disabled={searching}
              aria-expanded={isOpen}
              data-scope-nav=""
              data-scope-account={account.id}
              onClick={() => onToggleExpanded(account.id)}
              className={cn(ROW, "h-10 pl-1.5", ROW_INTERACTIVE, "disabled:cursor-default")}
            >
              <span className="flex size-4 shrink-0 items-center justify-center">
                <Chevron expanded={isOpen} />
              </span>
              <span className="flex min-w-0 flex-1 flex-col gap-px">
                <span
                  className={cn(
                    "truncate text-xs font-semibold",
                    account.needsReconnect ? "text-placeholder" : "text-ink",
                  )}
                >
                  {account.name}
                </span>
                <span className="truncate text-badge text-muted-foreground tabular-nums">
                  {accountSubtitle(entry, searching, labels)}
                </span>
              </span>
              {account.needsReconnect ? <ReconnectBadge label={t("shopScope.reconnect")} /> : null}
            </button>
            {isOpen
              ? entry.shops.map((shop) => (
                  <SingleShopRow
                    key={shop.shopId}
                    shop={shop}
                    account={account}
                    selected={shop.shopId === value}
                    disabled={disabled}
                    labels={labels}
                    onPick={onPick}
                  />
                ))
              : null}
          </div>
        );
      })}
      {filtered.ungrouped.map((shop) => (
        <SingleShopRow
          key={shop.shopId}
          shop={shop}
          account={null}
          selected={shop.shopId === value}
          disabled={disabled}
          labels={labels}
          onPick={onPick}
        />
      ))}
    </>
  );
}

function SingleShopRow({
  shop,
  account,
  selected,
  disabled,
  labels,
  onPick,
}: {
  shop: ScopeShop;
  account: ScopeAccount | null;
  selected: boolean;
  disabled: boolean;
  labels: ScopeTreeLabels;
  onPick: (shopId: string) => void;
}) {
  const blocked = disabled || shop.needsReconnect;
  return (
    // biome-ignore lint/a11y/useSemanticElements: a Select-style row with a check lane, announced as a radio choice.
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      aria-label={shopAccessibleName(shop, labels)}
      disabled={blocked}
      data-scope-nav=""
      onClick={() => onPick(shop.shopId)}
      className={cn(
        ROW,
        "h-8 pl-7.5",
        selected && "bg-control-highlight font-medium",
        blocked ? "cursor-default" : ROW_INTERACTIVE,
      )}
    >
      {shop.marketplace ? (
        <MarketplaceBadge
          code={shop.marketplace}
          className={cn((selected || !blocked) && BADGE_ON_HIGHLIGHT, selected && "bg-surface")}
        />
      ) : null}
      <span className="flex min-w-0 flex-1 items-baseline gap-1.5">
        <span
          className={cn("truncate text-xs", shop.needsReconnect ? "text-placeholder" : "text-ink")}
        >
          {shopPrimaryText(shop, account, labels)}
        </span>
        {shop.domain ? (
          <span className="truncate text-badge font-normal text-muted-foreground">
            {shop.domain}
          </span>
        ) : null}
      </span>
      {shop.needsReconnect && !account?.needsReconnect ? (
        <ReconnectBadge label={labels.t("shopScope.reconnect")} />
      ) : null}
      <span className="flex size-5 shrink-0 items-center justify-center" aria-hidden>
        {selected ? <IconCheck className="size-4 text-ink" stroke={2} /> : null}
      </span>
    </button>
  );
}
